import { useTranslation } from 'react-i18next';
import { topUsecaseAssets } from '@/constants/topUsecase';
import { useMemo } from 'react';

type UsecaseSectionTranslation = {
    title: string;
    description: string;
};

type UsecaseTranslation = {
    title: string;
    sections?: UsecaseSectionTranslation[];
    paragraphs?: string[];
    bullets?: string[];
    analysisNote?: string;
    imageAlt: string;
    imageNote?: string;
};

function TopUsecases() {
    const { t } = useTranslation('top');
    const sectionTitle = t('sections.usecases');

    const usecases = useMemo(() => {
        const translations = t('usecases.items', {
            returnObjects: true,
        }) as UsecaseTranslation[];
        return translations.map((item, index) => {
            const asset = topUsecaseAssets[index];
            return {
                ...item,
                image: asset?.image ?? '',
            };
        });
    }, [t]);

    return (
        <section className="relative py-16 lg:py-24 bg-white">
            {/* Decorative Wave Divider */}
            <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
                <svg className="relative block w-full h-12 lg:h-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                    <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-gray-50"></path>
                </svg>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-12 lg:pt-16">
                <h2 className="text-center text-3xl lg:text-5xl font-bold tracking-widest mb-16 lg:mb-24 text-gray-900">
                    {sectionTitle}
                </h2>

                <div className="space-y-24 lg:space-y-32">
                    {usecases.map((item, index) => (
                        <div
                            key={index}
                            className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-16 items-center`}
                        >
                            {/* Text Content */}
                            <div className="flex-1 space-y-6">
                                <div className="inline-block px-4 py-1 bg-kotomilThemaColor/30 rounded-full text-sm font-bold tracking-wider text-gray-800 mb-2">
                                    USE CASE 0{index + 1}
                                </div>
                                <h3 className="text-2xl lg:text-4xl font-bold text-gray-900 leading-tight">
                                    {item.title}
                                </h3>

                                {item.sections?.map((section, sIndex) => (
                                    <div key={sIndex} className="bg-gray-50 p-6 rounded-lg border border-gray-100">
                                        <h4 className="font-bold text-lg mb-2 text-gray-800">{section.title}</h4>
                                        <p className="text-gray-600 leading-relaxed">{section.description}</p>
                                    </div>
                                ))}

                                {item.paragraphs?.map((paragraph, pIndex) => (
                                    <p key={pIndex} className="text-lg text-gray-600 leading-relaxed">
                                        {paragraph}
                                    </p>
                                ))}

                                {item.bullets && item.bullets.length > 0 && (
                                    <ul className="list-disc list-inside space-y-2 text-gray-600 bg-gray-50 p-6 rounded-lg">
                                        {item.bullets.map((bullet, bIndex) => (
                                            <li key={bIndex} className="leading-relaxed">
                                                {bullet}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Image/Visual Content */}
                            <div className="flex-1 w-full">
                                <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-gray-100">
                                    {item.image && (
                                        <img
                                            src={item.image}
                                            alt={item.imageAlt}
                                            className="w-full h-auto object-cover image-protection transform hover:scale-105 transition-transform duration-700"
                                        />
                                    )}
                                    {item.analysisNote && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-white/10 text-gray-400 text-xs py-2 text-center backdrop-blur-sm">
                                            {item.analysisNote}
                                        </div>
                                    )}
                                </div>
                                {item.imageNote && (
                                    <p className="text-xs text-gray-500 text-center mt-2">
                                        {item.imageNote}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default TopUsecases;

